import { useState, useCallback, useEffect, useMemo } from 'react';
import { Application, DocumentPreview, ViewMode, TreeNode, Annotation, SignOff, SequenceStatus } from './types';
import { buildDocKey, subscribeToAppAnnotations } from './services/annotations';
import { subscribeToSignoffs } from './services/signoffs';
import { logAuditEvent } from './hooks/useAuditLog';
import { SignOffModal } from './components/SignOffModal';
import { useEctdParser } from './hooks/useEctdParser';
import { useTheme } from './hooks/useTheme';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useBookmarks } from './hooks/useBookmarks';
import { useAuth } from './contexts/AuthContext';
import { useAuditLog } from './hooks/useAuditLog';
import { useSession } from './hooks/useSession';
import { Toolbar } from './components/Toolbar';
import { TreePanel } from './components/TreePanel';
import { DocumentViewer } from './components/DocumentViewer';
import { StatusBar } from './components/StatusBar';
import { LoadingSpinner } from './components/LoadingSpinner';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ResizableSplitter } from './components/ResizableSplitter';
import { LoginForm } from './components/LoginForm';
import { SessionWarning } from './components/SessionWarning';
import { AdminRoleFix } from './components/AdminRoleFix';
import './App.css';

function App() {
  const [application, setApplication] = useState<Application | null>(null);
  const [currentSequence, setCurrentSequence] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>({ type: 'current' });
  const [selectedDocument, setSelectedDocument] = useState<DocumentPreview | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openCommentCounts, setOpenCommentCounts] = useState<Map<string, number>>(new Map());
  const [appAnnotations, setAppAnnotations] = useState<Annotation[]>([]);
  const [signoffs, setSignoffs] = useState<SignOff[]>([]);
  const [showSignOff, setShowSignOff] = useState(false);

  const { user, loading: authLoading, logout } = useAuth();
  const { parseApplications, isLoading: isParsing, error: parseError } = useEctdParser();
  const { theme, toggleTheme } = useTheme();
  const { bookmarks, addBookmark, removeBookmark, isBookmarked, clearBookmarks } = useBookmarks();
  const { logAction } = useAuditLog();
  
  // Session management
  const { 
    sessionInfo, 
    timeUntilTimeout, 
    showWarning, 
    extendSession, 
    forceLogout 
  } = useSession(user, logout);

  // Handle file selection
  const handleFileSelect = useCallback(async (files: FileList) => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      setError(null);
      const app = await parseApplications(files);
      setApplication(app);
      
      // Set default sequence to the latest one
      if (app.sequences.length > 0) {
        setCurrentSequence(app.sequences[0].id);
      }
      
      setViewMode({ type: 'current', sequenceId: app.sequences[0]?.id });
      
      // Detect file source (check if files have SharePoint metadata)
      const isSharePointSource = files.length > 0 && 
        (files[0] as any).__sharepointSource === true;
      
      // Log file selection with source tracking
      await logAction(user, isSharePointSource ? 'file_load_sharepoint' : 'file_load_local', {
        action: 'file_selection',
        fileCount: files.length,
        applicationId: app.id,
        sequenceCount: app.sequences.length,
        source: isSharePointSource ? 'sharepoint' : 'local'
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load application');
    } finally {
      setIsLoading(false);
    }
  }, [parseApplications, user, logAction]);

  // Handle document selection
  const handleDocumentSelect = useCallback(async (document: DocumentPreview) => {
    setSelectedDocument(document);
    
    if (user) {
      await logAction(user, 'document_view', {
        documentTitle: document.title,
        documentType: document.file.type,
        documentSize: document.file.size
      });
    }
  }, [user, logAction]);

  // Handle sequence change
  const handleSequenceChange = useCallback(async (sequenceId: string) => {
    setCurrentSequence(sequenceId);
    setViewMode({ type: 'current', sequenceId });
    
    if (user) {
      await logAction(user, 'sequence_change', {
        sequenceId,
        viewMode: 'current'
      });
    }
  }, [user, logAction]);

  const handleReset = useCallback(async () => {
    setApplication(null);
    setCurrentSequence(null);
    setSelectedDocument(null);
    setViewMode({ type: 'current' });
    setError(null);
    setIsLoading(false);
    clearBookmarks();

    if (user) {
      await logAction(user, 'dashboard_reset', {
        action: 'manual_reset'
      });
    }
  }, [clearBookmarks, logAction, user]);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    'ctrl+o': () => {
      const input = document.getElementById('file-input') as HTMLInputElement;
      input?.click();
    },
    'ctrl+f': () => {
      const searchInput = document.getElementById('search-input') as HTMLInputElement;
      searchInput?.focus();
    },
    'ctrl+t': async () => {
      toggleTheme();
      if (user) {
        await logAction(user, 'theme_change', {
          newTheme: theme.name === 'light' ? 'dark' : 'light'
        });
      }
    },
    'escape': () => {
      setSelectedDocument(null);
    },
  });

  // Helper function to filter out util folders and XML files
  const filterUtilFolders = useCallback((nodes: any[]): any[] => {
    return (nodes || [])
      .filter(n => n.name !== 'util')
      .map(n => {
        if (n.type === 'folder' && n.children) {
          return {
            ...n,
            children: filterUtilFolders(n.children)
          };
        }
        return n;
      });
  }, []);

  // Helper function to filter out XML files and index-md5.txt files
  const filterXMLFiles = useCallback((nodes: any[]): any[] => {
    return (nodes || [])
      .filter(n => {
        if (n.type === 'file') {
          const fileName = n.name.toLowerCase();
          // Filter out XML files and index-md5.txt files
          return !fileName.endsWith('.xml') && !fileName.includes('index-md5.txt');
        }
        return true;
      })
      .map(n => {
        if (n.type === 'folder' && n.children) {
          return {
            ...n,
            children: filterXMLFiles(n.children)
          };
        }
        return n;
      });
  }, []);

  // Merge sequences for cumulative view
  const mergeSequences = useCallback((sequences: any[]) => {
    if (!sequences || sequences.length === 0) return [];
    
    // Create a map to store all documents by their folder path
    const documentsByPath = new Map<string, any[]>();
    
    // Process all sequences and collect documents by their folder path
    sequences.forEach(sequence => {
      const collectDocuments = (nodes: any[], path: string[] = []) => {
        nodes.forEach((node: any) => {
          if (node.name === 'util') return; // Skip util folders
          
          if (node.type === 'file') {
            const fullPath = path.join('/');
            // Normalize folder names to merge similar folders
            const normalizedPath = fullPath
              .replace(/12-cover-letters/g, '12-cover-letter') // Merge cover-letters into cover-letter
              .replace(/12-cover-letter/g, '12-cover-letter'); // Keep cover-letter as is
            
            if (!documentsByPath.has(normalizedPath)) {
              documentsByPath.set(normalizedPath, []);
            }
            documentsByPath.get(normalizedPath)!.push({
              ...node,
              sequenceId: sequence.id
            });
          } else if (node.type === 'folder' && node.children) {
            // Skip sequence folders (0000, 0001, etc.) and go directly to their content
            if (/^\d{4}$/.test(node.name)) {
              collectDocuments(node.children, path); // Don't add sequence folder to path
            } else {
              collectDocuments(node.children, [...path, node.name]);
            }
          }
        });
      };
      
      if (sequence.tree) {
        collectDocuments(sequence.tree);
      }
    });
    
    // Build the merged tree structure using the first sequence as the base structure
    const buildMergedTree = (nodes: any[], currentPath: string[] = []): any[] => {
      return nodes.map((node: any) => {
        if (node.name === 'util') return null; // Skip util folders
        
        if (node.type === 'folder') {
          const newPath = [...currentPath, node.name];
          const fullPath = newPath.join('/');
          
          // Normalize the path to match the normalized document paths
          const normalizedPath = fullPath
            .replace(/12-cover-letters/g, '12-cover-letter') // Merge cover-letters into cover-letter
            .replace(/12-cover-letter/g, '12-cover-letter'); // Keep cover-letter as is
          
          // Get all documents from all sequences for this folder path
          const documents = documentsByPath.get(normalizedPath) || [];
          
          // Create merged children with ONLY documents from all sequences (no subfolders from base)
          const mergedChildren: any[] = [];
          
          // Add all documents from all sequences
          documents.forEach((doc: any) => {
            mergedChildren.push({
              type: 'file',
              name: doc.name,
              originalName: doc.originalName,
              file: doc.file,
              sequenceId: doc.sequenceId,
              lifecycleOp: doc.lifecycleOp || 'New',
              module: doc.module,
              xmlTitle: doc.xmlTitle,
              replacesSequence: doc.replacesSequence,
              isStf: doc.isStf,
              stfVersion: doc.stfVersion,
              stfStudyId: doc.stfStudyId
            });
          });
          
          // Recursively process subfolders (but don't include their documents, only structure)
          const subfolders = buildMergedTree(node.children || [], newPath);
          mergedChildren.push(...subfolders);
          
          return {
            ...node,
            children: mergedChildren
          };
        }
        return null; // Skip files from base structure - we only want the folder structure
      }).filter(Boolean);
    };
    
    // Use the most complete sequence as the base structure for folder hierarchy
    // Find the sequence with the most modules (m1, m2, m3, m4, m5)
    let bestSequence = sequences[0];
    let maxModules = 0;
    
    sequences.forEach((seq: any) => {
      const sequenceContent = seq.tree || [];
      const sequenceFolder = sequenceContent.find((node: any) => 
        node.type === 'folder' && /^\d{4}$/.test(node.name)
      );
      const content = sequenceFolder ? sequenceFolder.children || [] : sequenceContent;
      
      // Count modules in this sequence
      const moduleCount = content.filter((node: any) => 
        node.type === 'folder' && /^m[1-5]$/.test(node.name)
      ).length;
      
      if (moduleCount > maxModules) {
        maxModules = moduleCount;
        bestSequence = seq;
      }
    });
    
    const sequenceContent = bestSequence.tree || [];
    const sequenceFolder = sequenceContent.find((node: any) => 
      node.type === 'folder' && /^\d{4}$/.test(node.name)
    );
    
    const baseTree = sequenceFolder ? sequenceFolder.children || [] : sequenceContent;
    const mergedTree = buildMergedTree(baseTree);
    
    return mergedTree;
  }, []);

  // Get current tree based on view mode
  const getCurrentTree = useCallback((): TreeNode[] => {
    if (!application) return [];
    
    let tree: any[] = [];
    if (viewMode.type === 'cumulative') {
      tree = mergeSequences(application.sequences);
      // Apply XML filtering only for cumulative view
      tree = filterXMLFiles(tree);
    } else {
      const sequence = application.sequences.find(s => s.id === currentSequence);
      tree = sequence ? sequence.tree : [];
      // Don't filter XML files in regular view - show all files
    }
    
    // Filter out util folders from both views
    return filterUtilFolders(tree);
  }, [application, viewMode, currentSequence, mergeSequences, filterUtilFolders, filterXMLFiles]);

  const cloneNodes = useCallback((nodes: any[], seqId: string): TreeNode[] => {
    return (nodes || [])
      .filter(n => n.name !== 'util') // Filter out util folder
      .map(n => {
      if (n.type === 'folder') {
        return { 
          type: 'folder', 
          name: n.name, 
          expanded: n.expanded || false, 
          children: cloneNodes(n.children || [], seqId),
          metadata: n.metadata,
          xmlTitle: n.xmlTitle
        };
      } else {
        return { 
          type: 'file', 
          name: n.name, 
          originalName: n.originalName,
          file: n.file, 
          sequenceId: n.sequenceId || seqId,
          lifecycleOp: n.lifecycleOp || 'New',
          module: n.module,
          xmlTitle: n.xmlTitle,
          replacesSequence: n.replacesSequence,
          isStf: n.isStf,
          stfVersion: n.stfVersion,
          stfStudyId: n.stfStudyId
        };
      }
    });
  }, []);



  // Count documents in tree
  const countDocuments = useCallback((nodes: any[]): number => {
    let count = 0;
    (nodes || []).forEach(n => {
      if (n.type === 'file') {
        const fileName = (n.originalName || n.name || '').toLowerCase();
        const isXml = fileName.endsWith('.xml');
        const isMd5 = fileName.endsWith('.md5') || fileName.includes('index-md5');
        if (!isXml && !isMd5) {
          count++;
        }
      } else {
        count += countDocuments(n.children);
      }
    });
    return count;
  }, []);

  const currentTree = getCurrentTree();
  
  // Memoize document count to avoid recalculating on every render
  const documentCount = useMemo(() => {
    return countDocuments(currentTree);
  }, [currentTree]);
  
  // Get cumulative tree for document search - LAZY: only build when actually needed
  // Don't build it upfront to save memory - build it on-demand when external links are clicked
  // This is a function that builds the tree only when called
  const getCumulativeTree = useCallback(() => {
    if (!application || !application.sequences || application.sequences.length === 0) return [];
    // Only build cumulative tree if we have multiple sequences
    // For single sequence, use currentTree instead
    if (application.sequences.length === 1) {
      return application.sequences[0].tree || [];
    }
    return mergeSequences(application.sequences);
  }, [application?.sequences, mergeSequences]);
  
  // Only build cumulative tree if we're in cumulative view mode (for display)
  // Otherwise, build it lazily when needed for external link search
  const cumulativeTree = useMemo(() => {
    // Only build if we're actually in cumulative view (needed for display)
    if (viewMode.type === 'cumulative' && application) {
      return getCumulativeTree();
    }
    // Otherwise return empty array - will be built on-demand when searching
    return [];
  }, [viewMode.type, application, getCumulativeTree]);

  // Live annotations, application-wide: powers the tree comment badges and
  // the structured Excel export of all review comments.
  useEffect(() => {
    if (!user || !application) {
      setOpenCommentCounts(new Map());
      setAppAnnotations([]);
      return;
    }
    return subscribeToAppAnnotations(application.id, (annotations) => {
      setAppAnnotations(annotations);
      const counts = new Map<string, number>();
      annotations.forEach((a) => {
        if (!a.resolved) counts.set(a.docKey, (counts.get(a.docKey) || 0) + 1);
      });
      setOpenCommentCounts(counts);
    });
  }, [user, application]);

  // Live electronic sign-offs for the application
  useEffect(() => {
    if (!user || !application) {
      setSignoffs([]);
      return;
    }
    return subscribeToSignoffs(application.id, setSignoffs);
  }, [user, application]);

  // Review status of the current sequence = meaning of its most recent sign-off
  const sequenceStatus = useMemo((): SequenceStatus => {
    if (!currentSequence) return 'not_reviewed';
    const latest = signoffs.find((s) => s.sequenceId === currentSequence); // signoffs sorted newest-first
    return latest ? latest.meaning : 'not_reviewed';
  }, [signoffs, currentSequence]);

  // Export all review comments to a compact, styled Excel workbook
  const handleExportComments = useCallback(async () => {
    if (!application || !user) return;
    const XLSX = await import('xlsx-js-style');

    const moduleOf = (docPath: string): string => {
      const seg = docPath.toLowerCase().replace(/\\/g, '/').split('/').find((p) => /^m[1-5]$/.test(p));
      return seg ? seg.toUpperCase() : '';
    };

    const sorted = [...appAnnotations].sort(
      (a, b) =>
        a.sequenceId.localeCompare(b.sequenceId) ||
        moduleOf(a.docPath).localeCompare(moduleOf(b.docPath)) ||
        a.docTitle.localeCompare(b.docTitle) ||
        a.page - b.page ||
        a.createdAt.getTime() - b.createdAt.getTime()
    );
    const openCount = sorted.filter((a) => !a.resolved).length;

    const header = ['#', 'Sequence', 'Module', 'Document', 'Page', 'Comment', 'Author', 'Created', 'Status', 'Resolved By', 'Resolved At'];
    const meta = [
      ['Review Comments'],
      ['Application', application.id],
      ['Exported by', user.email],
      ['Exported at', new Date().toLocaleString()],
      ['Comments', `${sorted.length} total  ·  ${openCount} open  ·  ${sorted.length - openCount} resolved`],
      [],
    ];
    const rows = sorted.map((a, i) => [
      i + 1,
      a.sequenceId,
      moduleOf(a.docPath),
      a.docTitle,
      a.page,
      a.text,
      a.authorEmail,
      a.createdAt.toLocaleString(),
      a.resolved ? 'Resolved' : 'Open',
      a.resolvedBy || '',
      a.resolvedAt ? a.resolvedAt.toLocaleString() : '',
    ]);

    const ws = XLSX.utils.aoa_to_sheet([...meta, header, ...rows]);
    ws['!cols'] = [
      { wch: 4 },   // #
      { wch: 9 },   // Sequence
      { wch: 8 },   // Module
      { wch: 38 },  // Document
      { wch: 6 },   // Page
      { wch: 52 },  // Comment
      { wch: 24 },  // Author
      { wch: 17 },  // Created
      { wch: 9 },   // Status
      { wch: 24 },  // Resolved By
      { wch: 17 },  // Resolved At
    ];

    // Styling: compact 8pt table, dark header band, subtle grid, wrapped comments
    const FONT = 'Calibri';
    const thin = { style: 'thin' as const, color: { rgb: 'D9D9D9' } };
    const grid = { top: thin, bottom: thin, left: thin, right: thin };
    const headerRow = meta.length; // 0-based row index of the table header
    const range = XLSX.utils.decode_range(ws['!ref'] as string);
    const COMMENT_COL = 5;
    const STATUS_COL = 8;

    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
        if (!cell) continue;
        if (R === 0) {
          cell.s = { font: { name: FONT, sz: 11, bold: true, color: { rgb: '1F4E79' } } };
        } else if (R < headerRow) {
          cell.s = { font: { name: FONT, sz: 8, bold: C === 0, color: { rgb: C === 0 ? '595959' : '262626' } } };
        } else if (R === headerRow) {
          cell.s = {
            font: { name: FONT, sz: 8, bold: true, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '2F5597' } },
            alignment: { vertical: 'center', horizontal: 'center' },
            border: grid,
          };
        } else {
          cell.s = {
            font: { name: FONT, sz: 8, color: { rgb: '262626' } },
            border: grid,
            alignment: {
              vertical: 'top',
              wrapText: C === COMMENT_COL,
              horizontal: C === 0 || C === 4 ? 'center' : 'left',
            },
          };
          if (C === STATUS_COL) {
            cell.s.font = {
              name: FONT,
              sz: 8,
              bold: true,
              color: { rgb: cell.v === 'Open' ? 'B45309' : '15803D' },
            };
            cell.s.alignment = { vertical: 'top', horizontal: 'center' };
          }
        }
      }
    }

    // Filterable table header
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range({ s: { r: headerRow, c: 0 }, e: { r: range.e.r, c: range.e.c } }),
    };

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Review Comments');
    XLSX.writeFile(wb, `${application.id}_review_comments_${new Date().toISOString().split('T')[0]}.xlsx`);

    await logAuditEvent(user, 'comments_export', {
      applicationId: application.id,
      commentCount: sorted.length,
      openCount,
    });
  }, [application, user, appAnnotations]);

  const getOpenCommentCount = useCallback(
    (node: TreeNode): number => {
      if (node.type !== 'file' || !application) return 0;
      const fileWithPath = node.file as File & { webkitRelativePath?: string };
      const rawPath = (fileWithPath?.webkitRelativePath || node.originalName || node.name).toLowerCase();
      return openCommentCounts.get(buildDocKey(application.id, node.sequenceId, rawPath)) || 0;
    },
    [openCommentCounts, application]
  );

  const currentSequenceData = useMemo(() => {
    if (!application || !currentSequence) return null;
    return application.sequences.find(seq => seq.id === currentSequence) || null;
  }, [application, currentSequence]);

  const currentRegionalInfo = useMemo(() => {
    return currentSequenceData?.regionalInfo || application?.regionalInfo;
  }, [currentSequenceData, application]);

  // Warn user before refreshing if SharePoint files are loaded
  useEffect(() => {
    if (!application) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Check if any files are from SharePoint
      const hasSharePointFiles = currentTree.some(node => 
        checkNodeForSharePoint(node)
      );
      
      if (hasSharePointFiles) {
        e.preventDefault();
        e.returnValue = 'SharePoint files will need to be reloaded. Continue?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [application, currentTree]);

  // Check if a tree node or its children contain SharePoint files
  const checkNodeForSharePoint = (node: any): boolean => {
    if (node.type === 'file' && node.file && (node.file as any).__sharepointSource) {
      return true;
    }
    if (node.children) {
      return node.children.some((child: any) => checkNodeForSharePoint(child));
    }
    return false;
  };

  // Show loading screen while checking authentication
  if (authLoading) {
    return (
      <div className="app" data-theme={theme.name}>
        <LoadingSpinner />
      </div>
    );
  }

  // Show login form if not authenticated
  if (!user) {
    return (
      <div className="app" data-theme={theme.name}>
        <LoginForm />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="app" data-theme={theme.name}>
        {/* Admin role fix modal */}
        <AdminRoleFix />
        
        {/* Session warning */}
        {showWarning && (
          <SessionWarning
            timeUntilTimeout={timeUntilTimeout}
            onExtendSession={extendSession}
            onForceLogout={forceLogout}
          />
        )}

        <Toolbar
          onFileSelect={handleFileSelect}
          onSequenceChange={handleSequenceChange}
          onThemeToggle={async () => {
            toggleTheme();
            await logAction(user, 'theme_change', {
              newTheme: theme.name === 'light' ? 'dark' : 'light'
            });
          }}
          onReset={handleReset}
          application={application}
          currentSequence={currentSequence}
          theme={theme}
          onSignOff={() => setShowSignOff(true)}
          onExportComments={handleExportComments}
          commentCount={appAnnotations.length}
          sequenceStatus={sequenceStatus}
        />

        {showSignOff && application && currentSequence && (
          <SignOffModal
            appId={application.id}
            sequenceId={currentSequence}
            user={user}
            signoffs={signoffs}
            onClose={() => setShowSignOff(false)}
          />
        )}
        
        <StatusBar
          application={application}
          currentSequence={currentSequence}
          viewMode={viewMode}
          documentCount={documentCount}
          isLoading={isLoading || isParsing}
          error={error || parseError}
          user={user}
          sessionInfo={sessionInfo}
          regionalInfo={currentRegionalInfo}
        />

        <main className="main-content">
          <ResizableSplitter
            initialSizes={[40, 60]}
            minSizes={[25, 35]}
            direction="horizontal"
          >
            <TreePanel
              tree={currentTree}
              onDocumentSelect={handleDocumentSelect}
              onBookmarkToggle={async (node, path) => {
                if (isBookmarked(node)) {
                  removeBookmark(node);
                  if (user) {
                    await logAction(user, 'bookmark_remove', {
                      documentTitle: node.name,
                      documentType: node.type
                    });
                  }
                } else {
                  addBookmark(node, path);
                  if (user) {
                    await logAction(user, 'bookmark_add', {
                      documentTitle: node.name,
                      documentType: node.type
                    });
                  }
                }
              }}
              bookmarks={bookmarks}
              isBookmarked={isBookmarked}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              getOpenCommentCount={getOpenCommentCount}
            />
            
            <DocumentViewer
              document={selectedDocument}
              onClose={() => setSelectedDocument(null)}
              onDocumentSelect={handleDocumentSelect}
              tree={currentTree}
              allSequences={application?.sequences}
              cumulativeTree={cumulativeTree}
              getCumulativeTree={getCumulativeTree}
              appId={application?.id}
              annotations={appAnnotations}
              currentSequence={currentSequence}
              onExportComments={handleExportComments}
            />
          </ResizableSplitter>
        </main>

        {(isLoading || isParsing) && <LoadingSpinner />}
      </div>
    </ErrorBoundary>
  );
}

export default App;
